/** Trusted host for MultiPack sideload APK downloads. */
const TRUSTED_APK_HOST = 'dashboard.multipackpk.com'

export type TwaVersionDescriptor = {
  versionCode: number
  versionName: string
  minRequiredVersionCode: number
  apkUrl: string
  releaseNotes: string[]
  mandatory: boolean
}

function asPositiveInt(value: unknown): number | null {
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n) || n < 1 || !Number.isInteger(n)) return null
  return n
}

/** Reject anything that is not HTTPS on the MultiPack dashboard host. */
export function isTrustedApkUrl(url: string): boolean {
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:' && parsed.hostname === TRUSTED_APK_HOST
  } catch {
    return false
  }
}

export function parseTwaVersionDescriptor(raw: unknown): TwaVersionDescriptor | null {
  if (!raw || typeof raw !== 'object') return null
  const data = raw as Record<string, unknown>

  const versionCode = asPositiveInt(data.versionCode)
  const minRequiredVersionCode = asPositiveInt(data.minRequiredVersionCode) ?? 1
  const versionName =
    typeof data.versionName === 'string' && data.versionName.trim()
      ? data.versionName.trim()
      : versionCode
        ? String(versionCode)
        : ''
  const apkUrl = typeof data.apkUrl === 'string' ? data.apkUrl.trim() : ''
  const mandatory = data.mandatory === true
  const releaseNotes = Array.isArray(data.releaseNotes)
    ? data.releaseNotes.filter((line): line is string => typeof line === 'string' && Boolean(line.trim()))
    : []

  if (!versionCode || !versionName || !apkUrl || !isTrustedApkUrl(apkUrl)) return null

  return {
    versionCode,
    versionName,
    minRequiredVersionCode,
    apkUrl,
    releaseNotes,
    mandatory,
  }
}

/** Static public descriptor — not an authenticated API. */
export async function fetchLatestTwaVersion(): Promise<TwaVersionDescriptor | null> {
  try {
    const res = await fetch(`/twa-version.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
    if (!res.ok) return null
    return parseTwaVersionDescriptor(await res.json())
  } catch {
    return null
  }
}
